import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { usePhieuNhapHangBanTraLai } from '@/hooks/queries/usePhieuNhapHangBanTraLai';
import type { SearchValues } from '@/components/custom/erp/data-table-v2';
import { MA_CHUNG_TU, QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';

interface PhieuNhapHangBanTraLaiSearchContextValue {
  searchState: {
    filterParams: SearchValues;
    searchResults: BanHangRecord[];
    isSearching: boolean;
  };
  handleSearchChange: (values: SearchValues) => Promise<void>;
  resolvedRows: BanHangRecord[];
  transactionOptions: { value: string; label: string }[];
}

const PhieuNhapHangBanTraLaiSearchContext = createContext<PhieuNhapHangBanTraLaiSearchContextValue | null>(null);

export function usePhieuNhapHangBanTraLaiSearchContext() {
  const context = useContext(PhieuNhapHangBanTraLaiSearchContext);
  if (!context) {
    throw new Error('usePhieuNhapHangBanTraLaiSearchContext must be used within PhieuNhapHangBanTraLaiSearchProvider');
  }
  return context;
}

export function PhieuNhapHangBanTraLaiSearchProvider({ children }: { children: ReactNode }) {
  const [searchState, setSearchState] = useState<{
    filterParams: SearchValues;
    searchResults: BanHangRecord[];
    isSearching: boolean;
  }>({
    filterParams: {},
    searchResults: [],
    isSearching: false
  });

  const { filterPhieuNhapHangBanTraLai } = usePhieuNhapHangBanTraLai();

  const handleSearchChange = useCallback(async (values: SearchValues) => {
    const nextHasFilters = Object.keys(values || {}).length > 0;

    if (!nextHasFilters) {
      setSearchState({
        filterParams: {},
        searchResults: [],
        isSearching: false
      });
      return;
    }

    setSearchState(prev => ({
      ...prev,
      filterParams: values,
      isSearching: false,
      searchResults: []
    }));
  }, []);

  // Fetch filtered data when filterParams change
  useEffect(() => {
    const fetchFilteredData = async () => {
      if (!searchState.filterParams || Object.keys(searchState.filterParams).length === 0) {
        return;
      }

      setSearchState(prev => ({ ...prev, isSearching: true }));

      try {
        const transformedParams = Object.entries(searchState.filterParams).reduce((acc, [key, value]) => {
          acc[key] = Array.isArray(value) ? value : [value];
          return acc;
        }, {} as BanHangSearchParams);

        const res = await filterPhieuNhapHangBanTraLai(transformedParams);
        setSearchState(prev => ({
          ...prev,
          searchResults: res || [],
          isSearching: false
        }));
      } catch (error) {
        console.error('Error fetching filtered data:', error);
        setSearchState(prev => ({
          ...prev,
          searchResults: [],
          isSearching: false
        }));
      }
    };

    fetchFilteredData();
  }, [searchState.filterParams, filterPhieuNhapHangBanTraLai]);

  const resolvedRows = searchState.searchResults;

  const { data } = useCRUD({
    endpoint: `${QUERY_KEYS.MA_GIAO_DICH}?ma_ct=${MA_CHUNG_TU.BAN_HANG.PHIEU_NHAP_HANG_BAN_TRA_LAI}`
  });
  const transactionOptions = data.map(item => ({
    value: item.uuid,
    label: item.ten_gd
  }));

  return (
    <PhieuNhapHangBanTraLaiSearchContext.Provider
      value={{
        searchState,
        transactionOptions,
        handleSearchChange,
        resolvedRows
      }}
    >
      {children}
    </PhieuNhapHangBanTraLaiSearchContext.Provider>
  );
}
